/**
 * "From your past papers" (bug fix 1, spec 9 §5): what the writer takes from them — the
 * questions' shape, never their wording — summed up first, then every question grouped by the
 * paper it came from, in page order, filterable by command word and marks. A question can be
 * taken out of the patterns but never out of the copy check, so nothing here can weaken a paper.
 */
import { useMemo, useState } from "react";
import type { Exemplar, PastPaperListing } from "../api/api";
import { commandCounts, commandOf } from "./commands";
import { byPaper, caption, histogram, patterns } from "./summary";

type Props = {
  exemplars: Exemplar[];
  words: string[];
  /** The subject's past papers, for the groups' names; null until loaded (or if it failed). */
  listing: PastPaperListing | null;
  onPattern: (e: Exemplar, pattern: boolean) => void;
};

const SNIPPET = 140;
const TOP_WORDS = 5;
const CHART_HEIGHT = 72;

const plural = (n: number, one: string) => `${n} ${one}${n === 1 ? "" : "s"}`;
const yearOf = (s: string) => Number(/\b(?:19|20)\d\d\b/.exec(s)?.[0] ?? 0);

/** A group's name: the paper's session and reference, else its file's name. */
function namer(listing: PastPaperListing | null): (key: string) => string {
  const names = new Map<string, string>();
  for (const p of listing?.papers ?? []) {
    const name = p.series && !p.name.includes(p.series) ? `${p.series} · ${p.name}` : p.name;
    names.set(p.id, name);
    for (const d of p.documents) names.set(d.id, name);
  }
  for (const d of listing?.unmatched ?? []) names.set(d.id, d.filename);
  return (key) => names.get(key) ?? "A past paper";
}

function Question({
  e,
  words,
  onPattern,
}: {
  e: Exemplar;
  words: string[];
  onPattern: Props["onPattern"];
}) {
  const [whole, setWhole] = useState(false);
  const off = e.pattern === false;
  const long = e.text.length > SNIPPET;
  // Cut at a word, not inside one.
  const cut = e.text.slice(0, SNIPPET).replace(/\s+\S*$/, "");
  const text = whole || !long ? e.text : `${cut}…`;
  // As the counts read it, so a filter and the row it finds agree.
  const command = commandOf(e.text, words);
  return (
    <li className={off ? "past-q past-q--off" : "past-q"}>
      <div className="past-q__meta">
        {command && <span className="past-q__command">{command}</span>}
        <span className="num">{plural(e.marks, "mark")}</span>
        <span>page {e.page}</span>
      </div>
      <p className="past-q__text">
        {text}
        {long && (
          <button
            type="button"
            className="chapter__link past-q__more"
            onClick={() => setWhole(!whole)}
          >
            {whole ? "Less" : "More"}
          </button>
        )}
      </p>
      <div className="past-q__actions">
        {off && (
          <span className="past-q__off">Not used as a pattern · still checked for copying</span>
        )}
        <button
          type="button"
          className="chapter__link chapter__link--quiet"
          onClick={() => onPattern(e, off)}
        >
          {off ? "Use again" : "Don't use as a pattern"}
        </button>
      </div>
    </li>
  );
}

export function PastQuestions({ exemplars, words, listing, onPattern }: Props) {
  const [open, setOpen] = useState<Set<string>>(new Set());
  const [word, setWord] = useState<string | null>(null);
  const [marks, setMarks] = useState<number | null>(null);

  const used = patterns(exemplars);
  const hist = useMemo(() => histogram(exemplars), [exemplars]);
  // Every mark value from the lowest to the highest, so a gap shows as one.
  const bars = useMemo(() => {
    const by = new Map(hist.map((h) => [h.marks, h.count]));
    const lo = hist[0]?.marks ?? 0;
    const hi = hist.at(-1)?.marks ?? -1;
    return Array.from({ length: hi - lo + 1 }, (_, i) => ({
      marks: lo + i,
      count: by.get(lo + i) ?? 0,
    }));
  }, [hist]);
  const counts = useMemo(() => commandCounts(exemplars, words), [exemplars, words]);
  const name = useMemo(() => namer(listing), [listing]);
  const groups = useMemo(
    () =>
      byPaper(exemplars)
        .map((g, i) => ({ ...g, name: name(g.key), i }))
        .sort((a, b) => yearOf(b.name) - yearOf(a.name) || a.i - b.i),
    [exemplars, name],
  );

  if (exemplars.length === 0)
    return <p className="muted">No mark tags were found on the question paper.</p>;

  const most = Math.max(1, ...hist.map((h) => h.count));
  const top = counts.filter((c) => c.count > 0).slice(0, TOP_WORDS);
  const filtering = word !== null || marks !== null;
  const matches = (e: Exemplar) =>
    (word === null || commandOf(e.text, words) === word) && (marks === null || e.marks === marks);
  const left = exemplars.length - used.length;

  function toggle(key: string) {
    setOpen((s) => {
      const next = new Set(s);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  }

  return (
    <>
      <p className="kept__note">
        We read <strong>{plural(exemplars.length, "question")}</strong> from{" "}
        <strong>{plural(groups.length, "past paper")}</strong>. The writer copies none of their
        wording — it uses only their <strong>shape</strong>: the command word, the marks and the
        length. Every new paper is also checked against these questions, and any question too close
        to one of them is removed.
        {left > 0 &&
          ` ${plural(left, "question")} ${left === 1 ? "isn't" : "aren't"} used as a pattern.`}
      </p>

      {used.length > 0 && (
        <figure className="marks-chart">
          <ul aria-label="Questions by marks">
            {bars.map((h) => {
              const pct = Math.round((h.count / used.length) * 100);
              return (
                <li
                  key={h.marks}
                  title={`${plural(h.count, "question")} worth ${plural(h.marks, "mark")}`}
                >
                  <span className="marks-chart__pct num">{h.count > 0 ? `${pct}%` : ""}</span>
                  <span
                    className="marks-chart__bar"
                    style={{
                      height: h.count
                        ? `${Math.max(2, Math.round((h.count / most) * CHART_HEIGHT))}px`
                        : 0,
                    }}
                  />
                  <span className="marks-chart__marks num">{h.marks}</span>
                </li>
              );
            })}
          </ul>
          <figcaption>{caption(exemplars, words)}</figcaption>
        </figure>
      )}
      {top.length > 0 && (
        <p className="kept__top">
          Most used:{" "}
          {top.map((c, i) => (
            <span key={c.word}>
              {i > 0 && " · "}
              {c.word} <span className="num">{c.count}</span>
            </span>
          ))}
        </p>
      )}

      <div className="past-filters" role="group" aria-label="Filter the questions">
        {top.map((c) => (
          <button
            key={c.word}
            type="button"
            className="chip chip--filter"
            aria-pressed={word === c.word}
            onClick={() => setWord(word === c.word ? null : c.word)}
          >
            {c.word}
          </button>
        ))}
        {hist.map((h) => (
          <button
            key={h.marks}
            type="button"
            className="chip chip--filter"
            aria-pressed={marks === h.marks}
            onClick={() => setMarks(marks === h.marks ? null : h.marks)}
          >
            {plural(h.marks, "mark")}
          </button>
        ))}
      </div>

      <ul className="past-papers">
        {groups.map((g) => {
          const shown = filtering ? g.exemplars.filter(matches) : g.exemplars;
          if (filtering && shown.length === 0) return null;
          const isOpen = filtering || open.has(g.key);
          return (
            <li key={g.key} className="past-paper">
              <button
                type="button"
                className="past-paper__head"
                aria-expanded={isOpen}
                onClick={() => toggle(g.key)}
                disabled={filtering}
              >
                <span className="past-paper__name">{g.name}</span>
                <span className="past-paper__count num">
                  {filtering
                    ? `${shown.length} of ${plural(g.exemplars.length, "question")}`
                    : `${plural(g.exemplars.length, "question")} · ${plural(g.marks, "mark")}`}
                </span>
              </button>
              {isOpen && (
                <ul className="past-paper__list" aria-label={g.name}>
                  {shown.map((e) => (
                    <Question
                      key={`${e.document_id}:${e.page}:${e.text}`}
                      e={e}
                      words={words}
                      onPattern={onPattern}
                    />
                  ))}
                </ul>
              )}
            </li>
          );
        })}
      </ul>
    </>
  );
}
